import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Search, CheckCircle2, Check, Camera, X, ChevronDown, ChevronUp } from 'lucide-react'
import QrScanner from 'qr-scanner'

const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''

interface OperatorSession {
  operator_name: string
  event: {
    id: string
    title: string
    date: string | null
    location: string | null
  } | null
}

interface OperatorTicket {
  id: string
  ticket_code: string
  ticket_index: number
  is_checked_in: boolean
  checked_in_at: string | null
  checked_in_by: string | null
}

interface OperatorOrder {
  id: string
  order_code: string
  buyer_name: string
  buyer_phone: string
  quantity: number
  sale_event_phases: { name: string } | null
  tickets: OperatorTicket[]
  redeemed_count: number
  total_tickets: number
  is_checked_in: boolean
}

async function publicFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api/v1/public${path}`)
  const json = await res.json() as { success: boolean; data: T; error?: { code: string; message: string } }
  if (!res.ok) throw new Error(json.error?.message ?? `HTTP ${res.status}`)
  return json.data
}

async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api/v1/public${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = await res.json() as { success: boolean; data: T; error?: { code: string; message: string } }
  if (!res.ok) throw new Error(json.error?.message ?? `HTTP ${res.status}`)
  return json.data
}

// ─── QR Scanner Overlay ───────────────────────────────────────────────────────

function QRScannerOverlay({
  orders,
  onFound,
  onClose,
}: {
  orders: OperatorOrder[]
  onFound: (order: OperatorOrder, ticketCode: string) => void
  onClose: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerRef = useRef<QrScanner | null>(null)
  const [scanMsg, setScanMsg] = useState<{ type: 'error' | 'already'; text: string } | null>(null)
  const lastScanned = useRef<string | null>(null)

  useEffect(() => {
    if (!videoRef.current) return

    const scanner = new QrScanner(
      videoRef.current,
      (result) => {
        const code = result.data.trim().toUpperCase()

        // Debounce: ignore rapid re-scans of the same code
        if (code === lastScanned.current) return
        lastScanned.current = code
        setTimeout(() => { lastScanned.current = null }, 2000)

        let foundOrder: OperatorOrder | undefined
        let foundTicket: OperatorTicket | undefined
        for (const o of orders) {
          const t = o.tickets.find(t => t.ticket_code === code)
          if (t) { foundOrder = o; foundTicket = t; break }
        }
        if (!foundOrder || !foundTicket) {
          setScanMsg({ type: 'error', text: 'QR tidak dikenali — coba lagi' })
          return
        }
        if (foundTicket.is_checked_in) {
          setScanMsg({ type: 'already', text: `Tiket ${foundTicket.ticket_index} · ${foundOrder.buyer_name} sudah hadir` })
          return
        }

        // Valid ticket — hand off to parent, close scanner
        scanner.stop()
        onFound(foundOrder, foundTicket.ticket_code)
      },
      {
        returnDetailedScanResult: true,
        preferredCamera: 'environment',
        highlightScanRegion: false,
        highlightCodeOutline: false,
      }
    )

    scannerRef.current = scanner
    scanner.start().catch(() => {
      setScanMsg({ type: 'error', text: 'Tidak bisa mengakses kamera' })
    })

    return () => {
      scanner.stop()
      scanner.destroy()
    }
  }, [orders, onFound])

  function handleClose() {
    scannerRef.current?.stop()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-4 flex-shrink-0">
        <p className="text-sm font-semibold text-white">Scan QR Tiket</p>
        <button
          onClick={handleClose}
          className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Camera viewfinder */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="relative w-full max-w-xs aspect-square">
          {/* Live video */}
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-2xl"
            muted
            playsInline
          />

          {/* Corner frame overlay */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Top-left */}
            <span className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-yellow-400 rounded-tl-xl" />
            {/* Top-right */}
            <span className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-yellow-400 rounded-tr-xl" />
            {/* Bottom-left */}
            <span className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-yellow-400 rounded-bl-xl" />
            {/* Bottom-right */}
            <span className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-yellow-400 rounded-br-xl" />

            {/* Animated scan line */}
            <div className="absolute inset-x-4 top-4 bottom-4 overflow-hidden rounded-xl">
              <div className="w-full h-0.5 bg-yellow-400/70 animate-scan-line" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom area */}
      <div className="px-6 py-8 flex-shrink-0 text-center space-y-3">
        {scanMsg ? (
          <div className={`rounded-xl px-4 py-3 text-sm font-medium ${
            scanMsg.type === 'error'
              ? 'bg-red-900/40 border border-red-700 text-red-300'
              : 'bg-amber-900/40 border border-amber-700 text-amber-300'
          }`}>
            {scanMsg.text}
          </div>
        ) : (
          <p className="text-zinc-500 text-sm">Arahkan kamera ke QR code pembeli</p>
        )}
      </div>
    </div>
  )
}

// ─── Checkin Modal ────────────────────────────────────────────────────────────

function CheckinModal({
  order,
  ticketCode,
  processing,
  error,
  onConfirm,
  onClose,
}: {
  order: OperatorOrder
  ticketCode: string
  processing: boolean
  error: string | null
  onConfirm: () => void
  onClose: () => void
}) {
  const [verified, setVerified] = useState(false)
  const [handed, setHanded] = useState(false)
  const canSubmit = verified && handed && !processing

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70" />

      <div
        className="relative w-full sm:max-w-sm bg-zinc-900 rounded-t-2xl sm:rounded-2xl border border-zinc-800 p-6 space-y-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="sm:hidden w-10 h-1 bg-zinc-700 rounded-full mx-auto -mt-2 mb-1" />

        <div>
          <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-1">Check-In</p>
          <p className="text-lg font-bold text-white">{order.buyer_name}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="font-mono text-xs text-zinc-500">{order.order_code}</span>
            {order.sale_event_phases && (
              <span className="text-[10px] text-zinc-500 border border-zinc-700 rounded px-1.5 py-0.5">
                {order.sale_event_phases.name}
              </span>
            )}
            {order.tickets.length > 1 && (() => {
              const ticket = order.tickets.find(t => t.ticket_code === ticketCode)
              return ticket ? (
                <span className="text-[10px] text-zinc-400 font-mono">Tiket {ticket.ticket_index} dari {order.tickets.length}</span>
              ) : null
            })()}
          </div>
        </div>

        <div className="space-y-3">
          <ToggleButton
            checked={verified}
            onToggle={() => setVerified(v => !v)}
            label="Orang yang mengambil tiket sudah diverifikasi"
          />
          <ToggleButton
            checked={handed}
            onToggle={() => setHanded(h => !h)}
            label="Tiket sudah diserahkan ke pemegang yang benar"
          />
        </div>

        {error && (
          <p className="rounded-lg border border-red-800 bg-red-900/20 px-3 py-2 text-xs text-red-400">{error}</p>
        )}

        <div className="space-y-2 pt-1">
          <button
            onClick={onConfirm}
            disabled={!canSubmit}
            className="w-full py-4 text-sm font-bold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-green-600 hover:bg-green-500 active:bg-green-700 text-white"
          >
            {processing ? 'Memproses…' : 'Lakukan Check In'}
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

function ToggleButton({
  checked,
  onToggle,
  label,
}: {
  checked: boolean
  onToggle: () => void
  label: string
}) {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl border-2 text-left transition-all active:scale-[0.98] ${
        checked
          ? 'border-green-600 bg-green-900/25 text-green-300'
          : 'border-zinc-700 bg-zinc-800/50 text-zinc-300 hover:border-zinc-500'
      }`}
    >
      <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center border-2 transition-all ${
        checked
          ? 'border-green-500 bg-green-600'
          : 'border-zinc-600 bg-transparent'
      }`}>
        {checked && <Check size={13} strokeWidth={3} className="text-white" />}
      </div>
      <span className="text-sm font-medium leading-snug">{label}</span>
    </button>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CheckinOperatorPage() {
  const { token } = useParams<{ token: string }>()

  const [session, setSession] = useState<OperatorSession | null>(null)
  const [orders, setOrders] = useState<OperatorOrder[]>([])
  const [loadingSession, setLoadingSession] = useState(true)
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [invalidToken, setInvalidToken] = useState(false)
  const [search, setSearch] = useState('')
  const [scannerOpen, setScannerOpen] = useState(false)
  const [confirmingOrder, setConfirmingOrder] = useState<OperatorOrder | null>(null)
  const [confirmingTicketCode, setConfirmingTicketCode] = useState<string | null>(null)
  const [checkingIn, setCheckingIn] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  function toggleExpand(orderId: string) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  useEffect(() => {
    if (!token) return
    publicFetch<OperatorSession>(`/checkin/${token}`)
      .then(setSession)
      .catch(() => setInvalidToken(true))
      .finally(() => setLoadingSession(false))
  }, [token])

  useEffect(() => {
    if (!session || !token) return
    setLoadingOrders(true)
    publicFetch<OperatorOrder[]>(`/checkin/${token}/orders`)
      .then(setOrders)
      .finally(() => setLoadingOrders(false))
  }, [session, token])

  async function handleCheckin() {
    if (!token || !confirmingOrder || !confirmingTicketCode) return
    setCheckingIn(true)
    setModalError(null)
    try {
      await publicPost(`/checkin/${token}/checkin`, { ticket_code: confirmingTicketCode })
      setOrders(prev => prev.map(o =>
        o.id === confirmingOrder!.id
          ? {
              ...o,
              tickets: o.tickets.map(t =>
                t.ticket_code === confirmingTicketCode
                  ? { ...t, is_checked_in: true, checked_in_at: new Date().toISOString(), checked_in_by: session?.operator_name ?? null }
                  : t
              ),
              redeemed_count: o.redeemed_count + 1,
              is_checked_in: o.total_tickets === o.redeemed_count + 1,
            }
          : o
      ))
      setConfirmingOrder(null)
      setConfirmingTicketCode(null)
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Gagal check-in. Coba lagi.')
    } finally {
      setCheckingIn(false)
    }
  }

  function handleScanFound(order: OperatorOrder, ticketCode: string) {
    setScannerOpen(false)
    setConfirmingOrder(order)
    setConfirmingTicketCode(ticketCode)
    setModalError(null)
  }

  const q = search.trim().toLowerCase()
  const filtered = useMemo(() =>
    orders.filter(o =>
      !q ||
      o.buyer_name.toLowerCase().includes(q) ||
      o.order_code.toLowerCase().includes(q) ||
      o.buyer_phone.includes(q)
    ),
    [orders, q]
  )

  const sorted = useMemo(() => [
    ...filtered.filter(o => !o.is_checked_in),
    ...filtered.filter(o => o.is_checked_in),
  ], [filtered])

  const checkedCount = orders.reduce((sum, o) => sum + o.redeemed_count, 0)
  const totalTickets = orders.reduce((sum, o) => sum + o.total_tickets, 0)

  if (loadingSession) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <span className="text-zinc-500 text-sm">Memuat…</span>
      </div>
    )
  }

  if (invalidToken || !session) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-900/30 border border-red-800 flex items-center justify-center mb-4">
          <span className="text-red-400 text-xl">✕</span>
        </div>
        <h1 className="text-white font-bold text-lg mb-2">Sesi Tidak Valid</h1>
        <p className="text-zinc-500 text-sm leading-relaxed">
          Sesi ini sudah tidak aktif atau link tidak valid.<br />Hubungi admin untuk mendapatkan link baru.
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">

      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-4 flex-shrink-0">
        <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-1">
          {session.event?.date ?? ''}
        </p>
        <h1 className="text-base font-bold text-white leading-snug">{session.event?.title ?? 'Event'}</h1>
        <p className="text-xs text-zinc-500 mt-1">Operator: <span className="text-zinc-300">{session.operator_name}</span></p>
      </div>

      {/* Progress bar */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-zinc-500">{checkedCount} / {totalTickets} sudah check-in</span>
          {totalTickets > 0 && (
            <span className="text-xs font-semibold text-white">{Math.round((checkedCount / totalTickets) * 100)}%</span>
          )}
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-300"
            style={{ width: totalTickets > 0 ? `${(checkedCount / totalTickets) * 100}%` : '0%' }}
          />
        </div>
      </div>

      {/* Search bar with camera button */}
      <div className="px-4 py-3 flex-shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cari nama, kode order, atau nomor HP…"
            className="w-full bg-zinc-800 text-white rounded-xl pl-9 pr-10 py-3 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
          />
          <button
            onClick={() => setScannerOpen(true)}
            title="Scan QR tiket"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-yellow-400 transition-colors active:scale-90"
          >
            <Camera size={16} />
          </button>
        </div>
      </div>

      {/* Order list */}
      <div className="flex-1 px-4 pb-8">
        {loadingOrders ? (
          <p className="text-center text-zinc-500 text-sm py-8">Memuat daftar…</p>
        ) : sorted.length === 0 ? (
          <p className="text-center text-zinc-600 text-sm py-8">
            {orders.length === 0 ? 'Belum ada tiket yang dikonfirmasi.' : 'Tidak ada hasil.'}
          </p>
        ) : (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            <div className="divide-y divide-zinc-800">
              {sorted.map(o => (
                <div key={o.id} className={o.is_checked_in ? 'bg-green-900/10' : ''}>
                  {/* Order header */}
                  <div className="flex items-center justify-between px-4 py-4 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${o.is_checked_in ? 'text-zinc-400' : 'text-white'}`}>
                          {o.buyer_name}
                        </span>
                        {o.sale_event_phases && (
                          <span className="text-[10px] text-zinc-500 border border-zinc-700 rounded px-1.5 py-0.5">
                            {o.sale_event_phases.name}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-zinc-600 mt-0.5 font-mono">{o.order_code}</p>
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      {o.is_checked_in ? (
                        <div className="flex items-center gap-1.5 text-green-400">
                          <CheckCircle2 size={16} />
                          <span className="text-xs font-semibold">Hadir</span>
                        </div>
                      ) : o.tickets.length === 1 ? (
                        <button
                          onClick={() => { setConfirmingOrder(o); setConfirmingTicketCode(o.tickets[0]!.ticket_code); setModalError(null) }}
                          className="px-4 py-2 text-xs font-semibold bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors active:scale-95"
                        >
                          Check In
                        </button>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-zinc-500">{o.redeemed_count}/{o.total_tickets} hadir</span>
                          <button
                            onClick={() => toggleExpand(o.id)}
                            className="text-zinc-500 hover:text-zinc-300 transition-colors"
                          >
                            {expanded.has(o.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Per-ticket sub-rows for multi-ticket orders */}
                  {o.tickets.length > 1 && expanded.has(o.id) && o.tickets.map(t => (
                    <div key={t.id} className="flex items-center justify-between px-4 py-2 pl-8 bg-zinc-800/20 border-t border-zinc-800/40">
                      <span className="text-[11px] text-zinc-500 font-mono">
                        Tiket {t.ticket_index} · {t.ticket_code}
                      </span>
                      {t.is_checked_in ? (
                        <div className="flex items-center gap-1 text-green-400">
                          <CheckCircle2 size={13} />
                          <span className="text-[11px]">Hadir</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setConfirmingOrder(o); setConfirmingTicketCode(t.ticket_code); setModalError(null) }}
                          className="px-3 py-1 text-[11px] font-semibold bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors active:scale-95"
                        >
                          Check In
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* QR scanner overlay */}
      {scannerOpen && (
        <QRScannerOverlay
          orders={orders}
          onFound={handleScanFound}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {/* Check-in confirmation modal */}
      {confirmingOrder && confirmingTicketCode && (
        <CheckinModal
          order={confirmingOrder}
          ticketCode={confirmingTicketCode}
          processing={checkingIn}
          error={modalError}
          onConfirm={handleCheckin}
          onClose={() => { setConfirmingOrder(null); setConfirmingTicketCode(null); setModalError(null) }}
        />
      )}
    </div>
  )
}
